import { getEnv, types } from 'mobx-state-tree'

import { UPLOAD_EVENTS, readUploadStream, uploadStatement } from '../api/investmentPdf'

const { model } = types

// The disclaimer is a round trip out of a review, not an exit from it.
const AI_DISCLAIMER_PATH = '/ai-disclaimer'

export const UPLOAD_STATUS = {
  idle: 'idle',
  uploading: 'uploading',
  uploaded: 'uploaded',
  extracting: 'extracting',
  processing: 'processing',
  success: 'success',
  // The document was read but nothing usable came out of it — the user can try another file.
  error: 'error',
  // The request itself failed, so retrying the same file will not help.
  backendError: 'backend-error',
}

const IN_PROGRESS_STATUSES = [
  UPLOAD_STATUS.uploading,
  UPLOAD_STATUS.uploaded,
  UPLOAD_STATUS.extracting,
  UPLOAD_STATUS.processing,
]

const FINISHED_STATUSES = [UPLOAD_STATUS.success, UPLOAD_STATUS.error, UPLOAD_STATUS.backendError]

const EVENT_TO_STATUS = {
  [UPLOAD_EVENTS.UPLOADED]: UPLOAD_STATUS.uploaded,
  [UPLOAD_EVENTS.EXTRACTING_INVESTMENTS]: UPLOAD_STATUS.extracting,
  [UPLOAD_EVENTS.PROCESSING_INVESTMENTS]: UPLOAD_STATUS.processing,
  [UPLOAD_EVENTS.DOCUMENT_COMPLETED]: UPLOAD_STATUS.success,
  [UPLOAD_EVENTS.DOCUMENT_FAILED]: UPLOAD_STATUS.error,
}

/**
 * Institutional (employer plan) accounts are uploaded through their own endpoint.
 *
 * Derived from the route type in one place so no caller has to remember to pass it.
 */
export const isInstitutionalType = type => String(type).toLowerCase() === 'institutional'

/** Path of the Add/Review investments screen a finished upload hands its review to. */
export const investmentsReviewPath = ({ type, accountType, accountId }) =>
  `/accounts/add/${type}/${accountType}/${accountId}/investments`

/**
 * Tracks AI statement uploads for the whole app.
 *
 * An upload outlives the screen that started it: the user can dismiss the modal, navigate
 * anywhere, and still get a toast when processing finishes. That is why this lives on the
 * app store rather than in the accounts subtree.
 *
 * All records are keyed by account id and replaced wholesale so observers see the change.
 */
const InvestmentUpload = model({})
  .volatile(() => ({
    uploads: {},
    reviews: {},
    // Desktop modal params, or null when no modal is open.
    uploadModal: null,
    // Accounts whose upload progress is currently on screen (desktop modal or mobile page).
    // A finished upload only acts on its own when nothing is watching it.
    watchedUploads: {},
    // Bumped whenever a finished upload has refreshed an account's data, so screens showing
    // that account know to re-read its investments.
    investmentsRefreshedAt: {},
    // Last path the router reported, used to decide whether a review has been entered yet.
    currentPath: null,
  }))
  .views(self => ({
    getUpload(accountId) {
      return self.uploads[String(accountId)]
    },
    isAccountUploading(accountId) {
      const upload = self.uploads[String(accountId)]
      return Boolean(upload) && IN_PROGRESS_STATUSES.includes(upload.status)
    },
    isUploadFinished(accountId) {
      const upload = self.uploads[String(accountId)]
      return Boolean(upload) && FINISHED_STATUSES.includes(upload.status)
    },
    isUploadWatched(accountId) {
      return Boolean(self.watchedUploads[String(accountId)])
    },
    isInvestmentsReview(accountId) {
      return Boolean(self.reviews[String(accountId)]?.active)
    },
    investmentsRefreshCount(accountId) {
      return self.investmentsRefreshedAt[String(accountId)] ?? 0
    },
  }))
  .actions(self => ({
    setUpload(accountId, upload) {
      self.uploads = { ...self.uploads, [String(accountId)]: upload }
    },

    patchUpload(accountId, patch) {
      const key = String(accountId)
      const current = self.uploads[key]
      if (!current) return
      self.uploads = { ...self.uploads, [key]: { ...current, ...patch } }
    },

    clearUpload(accountId) {
      const key = String(accountId)
      if (!(key in self.uploads)) return
      const next = { ...self.uploads }
      delete next[key]
      self.uploads = next
    },

    /** Opens the desktop upload modal for an account. */
    openUploadModal({ type, accountType, accountId }) {
      self.uploadModal = { type, accountType, accountId, institutional: isInstitutionalType(type) }
    },

    closeUploadModal() {
      self.uploadModal = null
    },

    /**
     * Marks an account's upload as being watched on screen.
     *
     * Owned by the view rather than inferred from the modal state so it cannot be left behind
     * when the screen hosting the view goes away mid-upload. A stale registration would make a
     * finished upload think the user is still watching, and nothing would happen at all.
     */
    registerUploadView(accountId) {
      self.watchedUploads = { ...self.watchedUploads, [String(accountId)]: true }
    },

    unregisterUploadView(accountId) {
      const key = String(accountId)
      if (!(key in self.watchedUploads)) return
      const next = { ...self.watchedUploads }
      delete next[key]
      self.watchedUploads = next
    },

    setInvestmentsReview(accountId, active, path) {
      const key = String(accountId)
      const reviewPath = path ?? self.reviews[key]?.path
      self.reviews = {
        ...self.reviews,
        [key]: {
          active,
          path: reviewPath,
          // A review armed while the user is elsewhere survives until they arrive.
          entered: Boolean(reviewPath) && reviewPath === self.currentPath,
        },
      }
    },

    clearInvestmentsReview(accountId) {
      const key = String(accountId)
      if (!(key in self.reviews)) return
      const next = { ...self.reviews }
      delete next[key]
      self.reviews = next
    },

    /**
     * Ends any review whose screen the user has navigated away from.
     *
     * A review ends on navigation rather than on unmount: reloading investments unmounts and
     * remounts that screen, which is exactly when a finished upload hands the review over.
     */
    syncInvestmentsReviewPath(pathname) {
      self.currentPath = pathname

      if (pathname === AI_DISCLAIMER_PATH) return

      let next
      Object.entries(self.reviews).forEach(([key, review]) => {
        if (!review.path) return

        const onReviewScreen = pathname === review.path

        if (onReviewScreen && !review.entered) {
          next = next ?? { ...self.reviews }
          next[key] = { ...review, entered: true }
        } else if (!onReviewScreen && review.entered) {
          next = next ?? { ...self.reviews }
          delete next[key]
        }
      })

      if (next) self.reviews = next
    },

    navigateTo(path) {
      const navigate = getEnv(self).navigate
      if (!navigate) return
      navigate(path)
    },

    markInvestmentsRefreshed(accountId) {
      const key = String(accountId)
      self.investmentsRefreshedAt = {
        ...self.investmentsRefreshedAt,
        [key]: (self.investmentsRefreshedAt[key] ?? 0) + 1,
      }
    },

    /**
     * Starts an upload and returns immediately.
     *
     * The upload is deliberately not awaited so the user can dismiss the modal or leave the
     * screen while the document is processed.
     */
    startStatementUpload({ type, accountType, accountId, file }) {
      const institutional = isInstitutionalType(type)

      self.setUpload(accountId, {
        status: UPLOAD_STATUS.uploading,
        fileName: file?.name ?? '',
        error: null,
        type,
        accountType,
        accountId,
        institutional,
      })

      self.runStatementUpload({ accountId, file, institutional })
    },

    async runStatementUpload({ accountId, file, institutional }) {
      try {
        const stream = await uploadStatement({
          file,
          investmentAccountId: accountId,
          institutional,
        })
        await readUploadStream(stream, event => self.handleUploadEvent(accountId, event))
      } catch (err) {
        console.error('Statement upload failed', err)
        self.handleUploadFailure(accountId, err)
      }
    },

    handleUploadEvent(accountId, event) {
      const status = EVENT_TO_STATUS[event]
      if (!status || self.isUploadFinished(accountId)) return

      self.patchUpload(accountId, { status })

      if (status === UPLOAD_STATUS.success) {
        self.completeStatementUpload(accountId)
      } else if (status === UPLOAD_STATUS.error) {
        self.failStatementUpload(accountId)
      }
    },

    handleUploadFailure(accountId, err) {
      // A stream error that arrives after the document already succeeded or failed says nothing
      // new, and reporting it would replace a success the user is looking at with an error.
      if (self.isUploadFinished(accountId)) return

      self.patchUpload(accountId, {
        status: UPLOAD_STATUS.backendError,
        error: err instanceof Error ? err.message : 'Unknown error',
      })
      self.failStatementUpload(accountId)
    },

    /**
     * Handles a document that could not be used.
     *
     * When the failure is on screen the view shows it inline and owns the retry, so nothing is
     * toasted or cleared here.
     */
    failStatementUpload(accountId) {
      if (self.isUploadWatched(accountId)) return

      const upload = self.getUpload(accountId)
      const isBackendError = upload?.status === UPLOAD_STATUS.backendError

      self.showToast({
        severity: 'error',
        message: isBackendError
          ? 'Document upload failed. Try again later.'
          : 'Document upload failed. Try a different file.',
      })

      self.clearUpload(accountId)
    },

    /**
     * Handles a processed document.
     *
     * When the upload is on screen the user drives what happens next from it. Otherwise the
     * account's data is refreshed first, so the review screen the toast links to already has
     * the extracted investments by the time the user gets there.
     */
    async completeStatementUpload(accountId) {
      if (self.isUploadWatched(accountId)) return

      const upload = self.getUpload(accountId)
      if (!upload) return

      const reviewPath = investmentsReviewPath(upload)

      // Arm the review before refreshing: the toast links there either way, so the review
      // has to be waiting even if the refresh fails.
      self.setInvestmentsReview(accountId, true, reviewPath)

      await self.refreshAccountAfterUpload(accountId)

      self.showToast({
        severity: 'success',
        message: 'Document processed!',
        actionLabel: 'Review investments',
        onAction: () => self.navigateTo(reviewPath),
        autoHideDuration: 8000,
      })

      self.clearUpload(accountId)
    },

    /**
     * Refreshes what the app holds for an account after its statement was processed.
     *
     * Account balances come from the app store directly. Individual investments belong to the
     * accounts subtree, so screens showing this account re-read them off the bumped counter.
     */
    async refreshAccountAfterUpload(accountId) {
      try {
        await self.getAccounts()
      } catch (err) {
        // A failed refresh must not strand the upload — the screen re-fetches on mount.
        console.error('Could not refresh accounts after upload', err)
      }

      self.markInvestmentsRefreshed(accountId)
    },
  }))

export default InvestmentUpload
